import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROPERTY_DETAILS, GET_USERS, GET_PROPERTY_RESERVATIONS, UPDATE_PROPERTY_HVAC_FILTERS } from '../graphql/queries';
import { CREATE_WORK_ORDER, UPDATE_WORK_ORDER_STATUS } from '../graphql/mutations';
import { START_TIME_ENTRY } from '../graphql/work-order-time';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Person as PersonIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { navigateBackToProperties } from './Properties';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../utils/enums';
import HvacFiltersSection from '../components/properties/HvacFiltersSection';
import PropertyQuickLinks from '../components/properties/PropertyQuickLinks';
import CreateWorkOrderModal from '../components/work-orders/CreateWorkOrderModal';
import PropertyManagementDisplay from '../components/properties/PropertyManagementDisplay';
import usePropertyManagementDisplay from '../hooks/usePropertyManagementDisplay';
import CreateLostFoundModal from '../components/CreateLostFoundModal';
import WorkOrderDetailSlidePanel from '../components/work-orders/WorkOrderDetailSlidePanel';

// Property Area options
const PROPERTY_AREAS = [
  { value: 'KIAWAH', label: 'Kiawah Island' },
  { value: 'ISLE_OF_PALMS', label: 'Isle of Palms' },
  { value: 'ISLE_OF_PALMS_WILD_DUNES', label: 'Isle of Palms: Wild Dunes' },
  { value: 'SEABROOK_ISLAND', label: 'Seabrook Island' }
];

export default function ViewProperty() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  
  // Get the pagination state that was passed when navigating to view
  const returnToPage = location.state?.returnToPage || 1;
  const returnToSearch = location.state?.returnToSearch || '';
  const returnToAreas = location.state?.returnToAreas || [];

  // Add query to fetch users
  const { data: usersData } = useQuery(GET_USERS);

  // Get all users for use in dropdowns
  const allUsers = usersData?.users || [];

  // Add query to fetch property data
  const { data: propertyData, loading: propertyLoading, refetch } = useQuery(GET_PROPERTY_DETAILS, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first'
  });

  // Fetch current reservations for this property (for Current Guest Info modal)
  const { data: propertyReservationsData } = useQuery(GET_PROPERTY_RESERVATIONS, {
    variables: { propertyId: id },
    skip: !id,
    fetchPolicy: 'network-only'
  });

  const [property, setProperty] = useState(null);
  const [isGuestInfoModalOpen, setIsGuestInfoModalOpen] = useState(false);
  const [isCreateWorkOrderModalOpen, setIsCreateWorkOrderModalOpen] = useState(false);
  const [isCreateLostFoundModalOpen, setIsCreateLostFoundModalOpen] = useState(false);
  const [hvacFilters, setHvacFilters] = useState([]);
  const [hvacFiltersLastChanged, setHvacFiltersLastChanged] = useState(null);
  const [isSavingFilters, setIsSavingFilters] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isStartingWorkOrder, setIsStartingWorkOrder] = useState(false);
  const [slidePanelWorkOrderId, setSlidePanelWorkOrderId] = useState(null);
  const [isSlidePanelOpen, setIsSlidePanelOpen] = useState(false);
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false);
  const [isMobileActionsRendered, setIsMobileActionsRendered] = useState(false);
  const [isMobileActionsAnimating, setIsMobileActionsAnimating] = useState(false);

  const handleOpenMobileActions = () => {
    setIsMobileActionsRendered(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsMobileActionsAnimating(true));
    });
    setIsMobileActionsOpen(true);
  };

  const handleCloseMobileActions = () => {
    setIsMobileActionsAnimating(false);
    setIsMobileActionsOpen(false);
    setTimeout(() => setIsMobileActionsRendered(false), 300);
  };

  const [updatePropertyHvacFilters] = useMutation(UPDATE_PROPERTY_HVAC_FILTERS, {
    onCompleted: () => {
      toast.success('HVAC filters updated successfully');
      setIsSavingFilters(false);
      setHasUnsavedChanges(false);
      refetch();
    },
    onError: (error) => {
      toast.error('Failed to update filters: ' + (error.message || 'Unknown error'));
      setIsSavingFilters(false);
    }
  });

  const handleBackToProperties = () => {
    navigateBackToProperties(navigate, returnToPage, returnToSearch, returnToAreas);
  };

  const handleWorkOrderModalClose = (wasSuccessful) => {
    setIsCreateWorkOrderModalOpen(false);
    if (wasSuccessful) {
      toast.success('Work order created successfully');
    }
  };

  const handleLostFoundModalClose = (wasSuccessful) => {
    setIsCreateLostFoundModalOpen(false);
    if (wasSuccessful) {
      toast.success('Lost and found item created successfully');
    }
  };

  const [createWorkOrder] = useMutation(CREATE_WORK_ORDER);
  const [updateWorkOrderStatus] = useMutation(UPDATE_WORK_ORDER_STATUS);
  const [startTimeEntry] = useMutation(START_TIME_ENTRY);

  const handleStartWorkOrder = async () => {
    if (isStartingWorkOrder || !property?.id) return;
    setIsStartingWorkOrder(true);
    try {
      const { data: createData } = await createWorkOrder({
        variables: {
          input: {
            propertyId: property.id,
            description: 'Quick work order - description pending',
            isUrgent: false,
            workOrderType: 'GUEST_IMPACT',
            assignedRoles: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE],
          },
        },
      });
      const newWorkOrderId = createData?.createWorkOrder?.id;
      if (!newWorkOrderId) {
        toast.error('Failed to create work order');
        setIsStartingWorkOrder(false);
        return;
      }
      try {
        await updateWorkOrderStatus({
          variables: {
            id: newWorkOrderId,
            status: 'IN_PROGRESS',
            acceptedById: currentUser?.id,
          },
        });
      } catch (statusErr) {
        console.error('Failed to update status:', statusErr);
        toast.error('Work order created but status update failed');
      }
      try {
        await startTimeEntry({
          variables: { input: { workOrderId: newWorkOrderId } },
        });
      } catch (timerErr) {
        console.error('Failed to start timer:', timerErr);
        toast.error('Work order created but timer failed to start');
      }
      setSlidePanelWorkOrderId(newWorkOrderId);
      setIsSlidePanelOpen(true);
      toast.success('Work order started');
    } catch (err) {
      console.error('Failed to create work order:', err);
      toast.error('Failed to start work order: ' + (err.message || 'Unknown error'));
    } finally {
      setIsStartingWorkOrder(false);
    }
  };

  useEffect(() => {
    const nextProperty = propertyData?.property;
    if (!nextProperty?.id) return;
    setProperty(nextProperty);
    setHvacFilters(nextProperty.hvacFilters || []);
    setHvacFiltersLastChanged(
      nextProperty.hvacFiltersLastChanged
        ? new Date(nextProperty.hvacFiltersLastChanged)
        : null
    );
    setHasUnsavedChanges(false);
    // Hydrate once per property id. Depending on the Apollo result object
    // retriggers this effect whenever nested user records are merged into cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    propertyData?.property?.id,
    propertyData?.property?.propertyManagementTeamId,
    propertyData?.property?.propertyManagerId,
  ]);

  const handleHvacFiltersChange = (data) => {
    // Just update local state, don't save yet
    setHvacFilters(data.hvacFilters);
    setHvacFiltersLastChanged(
      data.hvacFiltersLastChanged 
        ? new Date(data.hvacFiltersLastChanged) 
        : null
    );
    setHasUnsavedChanges(true);
  };

  const handleSaveHvacFilters = async () => {
    if (isSavingFilters || !hasUnsavedChanges) {
      return;
    }
    
    setIsSavingFilters(true);
    try {
      await updatePropertyHvacFilters({
        variables: {
          id: property.id,
          hvacFilters: hvacFilters
            .map(({ __typename, id: filterId, filterLocation, filterSize }) => ({
              id: filterId?.startsWith('temp-') ? undefined : filterId,
              filterLocation: (filterLocation || '').trim(),
              filterSize: (filterSize || '').trim(),
            }))
            .filter((filter) => filter.filterLocation && filter.filterSize),
          hvacFiltersLastChanged: hvacFiltersLastChanged 
            ? hvacFiltersLastChanged.toISOString().split('T')[0] 
            : null
        }
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  // Helper function to get area label
  const getAreaLabel = (areaValue) => {
    const area = PROPERTY_AREAS.find(a => a.value === areaValue);
    return area ? area.label : areaValue;
  };

  // Helper function to get user name
  const getUserName = (userId) => {
    if (!userId) return 'Not assigned';
    const user = allUsers.find(u => u.id === userId);
    if (!user) return 'Unknown user';
    return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'Invalid date format';
    }
  };

  // Get current or most relevant reservation for this property (same logic as WorkOrderDetail)
  const getCurrentReservation = () => {
    const reservations = propertyReservationsData?.propertyReservations || [];
    if (!reservations.length) return null;
    const now = new Date();
    const current = reservations.find(res => {
      const checkIn = new Date(res.check_in);
      const checkOut = new Date(res.check_out);
      return checkIn <= now && now <= checkOut && !res.is_cancelled;
    });
    if (current) return current;
    const past = reservations
      .filter(res => new Date(res.check_out) < now && !res.is_cancelled)
      .sort((a, b) => new Date(b.check_out) - new Date(a.check_out));
    if (past.length > 0) return past[0];
    const upcoming = reservations
      .filter(res => new Date(res.check_in) > now && !res.is_cancelled)
      .sort((a, b) => new Date(a.check_in) - new Date(b.check_in));
    return upcoming.length > 0 ? upcoming[0] : null;
  };

  const currentReservation = getCurrentReservation();
  const { team: propertyManagementTeam, managers: displayPropertyManagers } = usePropertyManagementDisplay(
    property || propertyData?.property
  );

  // Show loading state if data is still loading
  if (propertyLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="container mx-auto p-2 md:p-4 max-w-7xl">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property Not Found</h1>
          <p className="text-gray-600 mb-6">The property you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBackToProperties}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 md:p-4 max-w-7xl">
      {/* Top row: Back + Actions (mobile) / inline buttons (desktop) */}
      <div className="mb-3 md:mb-6 flex flex-wrap items-center gap-3">
        <button
          onClick={handleBackToProperties}
          className="inline-flex items-center text-gray-300 md:text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer"
          type="button"
          aria-label="Back to Properties"
        >
          <ArrowLeft className="mr-1" />
          <span>Back to Properties</span>
        </button>

        {/* Mobile: Actions trigger button */}
        <button
          type="button"
          onClick={handleOpenMobileActions}
          className="md:hidden inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 border border-transparent shadow-sm"
          aria-label="Open actions menu"
        >
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Actions
        </button>

        {/* Desktop: inline action buttons */}
        <button
          type="button"
          onClick={() => setIsGuestInfoModalOpen(true)}
          className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-sky-600 text-white hover:bg-sky-700 border border-transparent"
          aria-label="View current guest info"
        >
          <PersonIcon className="mr-2 h-4 w-4" aria-hidden />
          Current Guest Info
        </button>
        <button
          type="button"
          onClick={() => setIsCreateWorkOrderModalOpen(true)}
          className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm"
          aria-label="Create work order"
        >
          <svg className="mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7h1a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h1m0 0V5a3 3 0 016 0v2m-3 10v-2m0 0l3-3m-3 3l-3-3" />
          </svg>
          Create Work Order
        </button>
        <button
          type="button"
          onClick={handleStartWorkOrder}
          disabled={isStartingWorkOrder}
          className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 border border-transparent shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Start work order"
        >
          {isStartingWorkOrder ? (
            <svg className="animate-spin mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
          {isStartingWorkOrder ? 'Starting...' : 'Start Work Order'}
        </button>
        <button
          type="button"
          onClick={() => setIsCreateLostFoundModalOpen(true)}
          className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 border border-transparent shadow-sm"
          aria-label="Report lost item"
        >
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
          </svg>
          Report Lost Item
        </button>
      </div>

      {/* Mobile Actions Slide-out Drawer */}
      {isMobileActionsRendered && (
        <>
          <div
            className={`md:hidden fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
              isMobileActionsAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
            }`}
            onClick={handleCloseMobileActions}
            aria-hidden="true"
          />
          <div
            className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
              isMobileActionsAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Actions menu"
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
              <button
                type="button"
                onClick={handleCloseMobileActions}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close actions menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="px-3 py-4 space-y-2">
              <button
                type="button"
                onClick={() => { handleStartWorkOrder(); handleCloseMobileActions(); }}
                disabled={isStartingWorkOrder}
                className="w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Start work order"
              >
                {isStartingWorkOrder ? (
                  <svg className="animate-spin mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="mr-3 h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
                {isStartingWorkOrder ? 'Starting...' : 'Start Work Order'}
              </button>
              <button
                type="button"
                onClick={() => { setIsCreateLostFoundModalOpen(true); handleCloseMobileActions(); }}
                className="w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
                aria-label="Report lost item"
              >
                <svg className="mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
                Report Lost Item
              </button>
              <button
                type="button"
                onClick={() => { setIsCreateWorkOrderModalOpen(true); handleCloseMobileActions(); }}
                className="w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                aria-label="Create work order"
              >
                <svg className="mr-3 h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7h1a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h1m0 0V5a3 3 0 016 0v2m-3 10v-2m0 0l3-3m-3 3l-3-3" />
                </svg>
                Create Work Order
              </button>
              <button
                type="button"
                onClick={() => { setIsGuestInfoModalOpen(true); handleCloseMobileActions(); }}
                className="w-full flex items-center px-3 py-3 rounded-lg text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 transition-colors"
                aria-label="View current guest info"
              >
                <PersonIcon className="mr-3 h-5 w-5" aria-hidden />
                Current Guest Info
              </button>
            </nav>
          </div>
        </>
      )}

      {/* Property title */}
      <div className="mb-4 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white md:text-gray-900">
          View Property: {property.name}
        </h1>
      </div>

      {/* Quick Links Navigation */}
      <PropertyQuickLinks
        sections={[
          { id: 'basic-info', label: 'Basic Info' },
          { id: 'check-in', label: 'Check-in' },
          { id: 'entry-details', label: 'Entry Details' },
          { id: 'property-details', label: 'Property Details' },
          { id: 'team-assignment', label: 'Team Assignment' },
          { id: 'notes', label: 'Notes' },
          { id: 'hvac-filters', label: 'HVAC Filters' },
          { id: 'staging-photos', label: 'Staging Photos' },
        ]}
      />

      {/* Basic Info Section */}
      <div id="basic-info" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Property Name</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.name || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Street Address</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.address || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">City</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.city || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">State</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.state || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Zipcode</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.zip || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Area</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {getAreaLabel(property.area) || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Check-in/out Times Section */}
      <div id="check-in" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Check-in Information</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Check-in Time</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.checkInTime || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Check-out Time</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.checkOutTime || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Entry Details Section */}
      <div id="entry-details" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Entry Details</h3>
        </div>
        <div className="p-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Access Instructions</label>
            <div className="px-4 py-3 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm min-h-[100px]">
              {property.entryDetails || 'No access instructions provided'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Property Details Section */}
      <div id="property-details" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Property Details</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Beds</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.beds || 'Not specified'}
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Baths</label>
              <div className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm">
                {property.baths || 'Not specified'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="team-assignment" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Team Assignment</h3>
        </div>
        <div className="p-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Property Management</label>
            <div className="px-4 py-4 w-full border border-gray-200 rounded-md bg-gray-50">
              <PropertyManagementDisplay
                team={propertyManagementTeam}
                managers={displayPropertyManagers}
                emptyLabel={getUserName(property.propertyManagerId)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Notes Section */}
      <div id="notes" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Notes</h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Housekeeping Notes</label>
            <div className="px-4 py-3 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm min-h-[80px]">
              {property.housekeepingNotes || 'No housekeeping notes'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Staging Notes</label>
            <div className="px-4 py-3 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm min-h-[80px]">
              {property.stagingNotes || 'No staging notes'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Property Manager Notes</label>
            <div className="px-4 py-3 w-full border border-gray-200 rounded-md bg-gray-50 text-gray-900 text-sm min-h-[80px]">
              {property.propertyManagerNotes || 'No property manager notes'}
            </div>
          </div>
        </div>
      </div>
      
      {/* HVAC Filters Section */}
      <div id="hvac-filters" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">HVAC Filters</h3>
              <p className="mt-1 text-sm text-gray-500">
                HVAC filter locations and sizes for this property
              </p>
            </div>
            {hasUnsavedChanges && (
              <button
                type="button"
                onClick={handleSaveHvacFilters}
                disabled={isSavingFilters}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSavingFilters ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </button>
            )}
          </div>
        </div>
        <div className="relative">
          {isSavingFilters && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-700">Saving...</span>
              </div>
            </div>
          )}
          <HvacFiltersSection
            hvacFilters={hvacFilters}
            hvacFiltersLastChanged={hvacFiltersLastChanged}
            onChange={handleHvacFiltersChange}
          />
        </div>
      </div>

      {/* Staging Photos Section */}
      {property.stagingPhotos && property.stagingPhotos.length > 0 && (
        <div id="staging-photos" className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 mb-8">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Staging Photos</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {property.stagingPhotos.map((photo, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={photo} 
                    alt={`Staging photo ${index + 1}`}
                    className="h-48 w-full object-cover rounded-lg"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Current Guest Info Modal */}
      {isGuestInfoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="guest-info-modal-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 id="guest-info-modal-title" className="text-lg font-semibold text-gray-900">
                Current Guest Info
              </h2>
              <button
                type="button"
                onClick={() => setIsGuestInfoModalOpen(false)}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close modal"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {currentReservation ? (
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{currentReservation.guest_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {currentReservation.mobile_phone ? (
                        <a href={`tel:${currentReservation.mobile_phone}`} className="text-blue-600 hover:text-blue-800">
                          {currentReservation.mobile_phone}
                        </a>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {currentReservation.email ? (
                        <a href={`mailto:${currentReservation.email}`} className="text-blue-600 hover:text-blue-800">
                          {currentReservation.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Check-in</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {currentReservation.check_in
                        ? `${format(new Date(currentReservation.check_in), 'MMM d, yyyy')}${currentReservation.check_in_time ? ` at ${currentReservation.check_in_time}` : ''}`
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Check-out</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {currentReservation.check_out
                        ? `${format(new Date(currentReservation.check_out), 'MMM d, yyyy')}${currentReservation.check_out_time ? ` at ${currentReservation.check_out_time}` : ''}`
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Reservation number</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">
                      {currentReservation.streamline_reservation_id || currentReservation.id || '—'}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="text-sm text-gray-500">No current or upcoming reservation found for this property.</p>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setIsGuestInfoModalOpen(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-sky-600 hover:bg-sky-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Work Order Modal */}
      <CreateWorkOrderModal
        isOpen={isCreateWorkOrderModalOpen}
        onClose={handleWorkOrderModalClose}
        propertyId={property?.id}
        propertyName={property?.name}
        reservationId={currentReservation?.id ?? undefined}
      />

      {/* Create Lost Found Modal */}
      <CreateLostFoundModal
        isOpen={isCreateLostFoundModalOpen}
        onClose={handleLostFoundModalClose}
        propertyId={property?.id}
        propertyName={property?.name}
        propertyData={property}
      />

      {/* Work Order Detail Slide Panel */}
      <WorkOrderDetailSlidePanel
        isOpen={isSlidePanelOpen}
        onClose={() => setIsSlidePanelOpen(false)}
        workOrderId={slidePanelWorkOrderId}
      />

      {/* Back button at bottom */}
      <div className="flex justify-end pt-8 border-t border-gray-200">
        <button
          type="button"
          onClick={handleBackToProperties}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back to Properties
        </button>
      </div>
    </div>
  );
}
